import L, { type LatLngTuple } from 'leaflet';
import { useEffect, useRef } from 'react';
import { Marker, Popup, useMap } from 'react-leaflet';

// How a map draws each tour — real road itineraries (IGN) or « vol d'oiseau » straight lines. « Vol d'oiseau »
// skips the routing fetches, so the routes update instantly while editing tours.
export type RouteMode = 'road' | 'crow';

export const ROUTE_MODES: { value: RouteMode; label: string }[] = [
    { value: 'crow', label: "Vol d'oiseau" },
    { value: 'road', label: 'Itinéraire' },
];

export const OSM_BASEMAP = {
    id: 'standard',
    label: 'Standard',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    subdomains: 'abc',
    pale: false,
} as const;

// Base map choices. "Clair" is the default: the coloured tour routes stand out far more than on the busy
// standard OSM tiles. Both are free and keyless — but CARTO now stamps « API KEY REQUIRED » across every
// unauthenticated tile, so "Clair" is IGN Géoplateforme (the service we already use for the road geometry),
// desaturated by CSS into the pale backdrop CARTO Positron used to give us. totops does the same.
export const BASEMAPS = [
    {
        id: 'clair',
        label: 'Clair',
        url: 'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&STYLE=normal&TILEMATRIXSET=PM&FORMAT=image/png&TILEMATRIX={z}&TILECOL={x}&TILEROW={y}',
        attribution: '&copy; <a href="https://www.ign.fr/">IGN</a>',
        subdomains: 'abc',
        pale: true,
    },
    OSM_BASEMAP,
] as const;

export type Basemap = (typeof BASEMAPS)[number];

// "In modification" colour: orange, deliberately outside the tour palette (cool/vivid hues), so a stop
// selected for a move stands out at a glance whatever its appro.
const EDIT_ACCENT = '#F97316';

type NumberedIconOptions = {
    size: number;
    /** Stop selected for a move (edit mode): its border (normally white) turns "modification" orange. */
    pending?: boolean;
    /** Pill under the disc (« Départ » / « Arrivée »). */
    tag?: string | null;
};

// Numbered disc marker in the tour colour.
export const numberedIcon = (
    color: string,
    number: number,
    { size, pending = false, tag = null }: NumberedIconOptions,
): L.DivIcon => {
    const ring = pending ? EDIT_ACCENT : 'white';
    const pill = tag
        ? `<span style="position:absolute;top:${size + 1}px;left:50%;transform:translateX(-50%);background:#fff;color:${color};border:1.5px solid ${color};border-radius:999px;padding:0 6px;font-size:9px;font-weight:800;letter-spacing:0.3px;text-transform:uppercase;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.35);">${tag}</span>`
        : '';
    return L.divIcon({
        className: '',
        html: `<div style="position:relative;width:${size}px;height:${size}px;">
            <span style="display:flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;background:${color};border:2px solid ${ring};border-radius:50%;box-shadow:0 1px 5px rgba(0,0,0,0.45);color:#fff;font-size:11px;font-weight:700;">${number}</span>
            ${pill}
        </div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -size / 2 - 4],
    });
};

// A house on a dark square, as in totops: the warehouse is where every round starts, not a numbered stop.
const DEPOT_SIZE = 24;
const depotIcon = L.divIcon({
    className: '',
    html: `<span style="display:flex;align-items:center;justify-content:center;width:${DEPOT_SIZE}px;height:${DEPOT_SIZE}px;background:#374151;border:2px solid white;border-radius:6px;box-shadow:0 1px 5px rgba(0,0,0,0.45);"><svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg></span>`,
    iconSize: [DEPOT_SIZE, DEPOT_SIZE],
    iconAnchor: [DEPOT_SIZE / 2, DEPOT_SIZE / 2],
    popupAnchor: [0, -DEPOT_SIZE / 2 - 4],
});

export type MapDepot = { name: string; address: string; latitude: number; longitude: number };

/** The warehouse the tours leave from — drawn on the Tournées map and on the dynamic planning's. */
export const DepotMarker = ({ depot }: { depot: MapDepot }): JSX.Element => (
    <Marker position={[depot.latitude, depot.longitude]} icon={depotIcon} zIndexOffset={500}>
        <Popup>
            <b>{depot.name}</b>
            <br />
            {depot.address}
        </Popup>
    </Marker>
);

// Fits the map to `points` whenever the SET of points changes — not on every render, and not on a reorder,
// which keeps the same points and must not throw the planner's zoom away. `once`: fit the first non-empty set
// only, then leave the zoom to the user whatever the data does (the tours map).
export const FitBounds = ({ points, once = false }: { points: LatLngTuple[]; once?: boolean }): null => {
    const map = useMap();
    const fitted = useRef(false);
    const signature = points
        .map((point) => `${point[0].toFixed(5)},${point[1].toFixed(5)}`)
        .sort()
        .join('|');
    useEffect(() => {
        if (!signature || (once && fitted.current)) return;
        fitted.current = true;
        map.fitBounds(points, { padding: [50, 50] });
        // `points` is intentionally out of deps: `signature` captures the only change that must refit.
    }, [signature, once, map]);
    return null;
};
